import { ReactNode, useEffect, useState } from "react";
import z from "zod";
import {
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	isEmpty,
} from "../../../utils/schema-fields";
import { cn } from "../../lib/utils";
import { Icon, PartialIndicator } from "../../primitives";
import { RequiredForApprovalIndicator } from "../../ui/RequiredForApprovalIndicator";
import { normalizeFormValues } from "../utils/formResolver";

type EntitySeverity = "error" | "requiredForApproval" | "partial";

export type EntityCardProps = {
	title: string;
	subtitle?: string;
	icon?: ReactNode;
	onEdit?: () => void;
	onRemove?: () => void;
	children?: ReactNode;
	condensed?: boolean;
	className?: string;
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	entity: Record<string, any>;
	schema: z.ZodType;
};

const getEntitySeverity = async (
	entity: Record<string, unknown>,
	schema: z.ZodType,
): Promise<EntitySeverity | null> => {
	const cleaned = normalizeFormValues(entity, { returnEmptyObject: true, emptyValue: undefined });
	const result = await schema.safeParseAsync(cleaned);

	if (!result.success) return "error";

	const partialPaths = getPartialFieldPaths(schema, cleaned);
	const reqForApprovalPaths = getRequiredForApprovalFieldPaths(schema, cleaned);

	const hasEmptyReqForApproval = reqForApprovalPaths.some((path) =>
		isEmpty(getNestedValue(cleaned, path)),
	);
	if (hasEmptyReqForApproval) return "requiredForApproval";

	const hasEmptyPartial = partialPaths.some((path) => isEmpty(getNestedValue(cleaned, path)));
	if (hasEmptyPartial) return "partial";

	return null;
};

/**
 * @deprecated Use `<Card elevation="raised">` from
 * `@portal/components/primitives/Card` instead. Part of the card consolidation.
 */
export const EntityCard = ({
	title,
	subtitle,
	icon,
	onEdit,
	onRemove,
	children,
	condensed,
	className,
	entity,
	schema,
}: EntityCardProps) => {
	const [severity, setSeverity] = useState<EntitySeverity | null>(null);

	useEffect(() => {
		const getSeverity = async () => {
			const result = await getEntitySeverity(entity, schema);
			setSeverity(result);
		};
		getSeverity();
	}, [entity, schema]);

	return (
		<div className="w-full flex flex-col space-y-[6px]">
			<div
				className={cn(
					"w-full h-[50px] flex items-center space-x-[15px] p-[15px] bg-white border rounded-[5px] shadow-sm",
					condensed && "h-[40px]",
					severity === "error" ? "border-danger" : "border-bc-default",
					onEdit && "cursor-pointer",
					className,
				)}
				onClick={onEdit}
			>
				{severity === "error" && (
					<div className="w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-inverse text-[10px] font-semibold">
						!
					</div>
				)}
				{severity === "requiredForApproval" && <RequiredForApprovalIndicator active={true} />}
				{severity === "partial" && <PartialIndicator active={true} />}
				{icon}
				<div
					className={cn(
						"flex-1 font-medium text-primary text-[15px] flex items-center gap-2",
						condensed && "text-[13.5px] font-[450]",
					)}
				>
					<span>{title || "Untitled"}</span>
					{subtitle && <span className="text-tertiary font-normal">{subtitle}</span>}
				</div>
				<div className="flex items-center space-x-[5px]">
					{onEdit && (
						<div className="p-[5px] hover:bg-gray-100 rounded">
							<Icon type="pencil-square" size={condensed ? "mini" : "small"} color="secondary" />
						</div>
					)}
					{onRemove && (
						<div
							className="p-[5px] hover:bg-surface-neutral rounded"
							data-testid="remove-entity"
							onClick={(e) => {
								e.stopPropagation();
								onRemove();
							}}
						>
							<Icon type="trash" size="small" color="danger" />
						</div>
					)}
				</div>
			</div>
			{children}
		</div>
	);
};
